/**
 * Safe HTML Utilities for Page Editor
 *
 * TASK 10.0: XSS Prevention utilities for history UIs
 *
 * Provides secure DOM construction patterns to prevent XSS attacks
 * when rendering user-generated content (block titles, editor names, etc.)
 *
 * Usage:
 *   import { escapeHtml, createSafeElement, createHistoryEntryElement } from './safe_html.js'
 *
 *   // Escape user content before interpolation
 *   const safeTitle = escapeHtml(userTitle)
 *
 *   // Create elements safely
 *   const span = createSafeElement('span', { class: 'title' }, userTitle)
 *
 *   // Create full history entry
 *   const entry = createHistoryEntryElement(entryData)
 */

import { createLogger } from './logger.js'

const logger = createLogger('SafeHtml')

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - The string to escape
 * @returns {string} - HTML-escaped string
 */
export function escapeHtml(str) {
  if (str == null) return ''
  const string = String(str)
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  }
  return string.replace(/[&<>"'`=/]/g, (char) => htmlEscapes[char])
}

/**
 * Create a DOM element with safe text content
 * @param {string} tagName - The tag name (e.g., 'span', 'div')
 * @param {Object} attributes - Object of attribute name/value pairs
 * @param {string|null} textContent - Text content (will be set safely via textContent)
 * @returns {HTMLElement} - The created element
 */
export function createSafeElement(tagName, attributes = {}, textContent = null) {
  const el = document.createElement(tagName)

  // Set attributes safely
  for (const [key, value] of Object.entries(attributes)) {
    if (value != null) {
      // Use setAttribute for safety (automatically escapes attribute values)
      el.setAttribute(key, String(value))
    }
  }

  // Set text content safely (textContent is always safe)
  if (textContent != null) {
    el.textContent = textContent
  }

  return el
}

/**
 * Create a history timeline entry element safely
 * Replaces innerHTML template string construction
 *
 * @param {Object} entry - History entry data
 * @param {string} entry.block_id - Block UUID
 * @param {string} entry.block_type - Block type
 * @param {string} entry.section_title - Section title (user content)
 * @param {number} entry.version - Version number
 * @param {string} entry.edited_at - ISO date string
 * @param {string} entry.edited_by_name - Editor name (user content)
 * @param {boolean} entry.is_current - Whether this is the current version
 * @param {Function} formatTimeOnly - Time formatter function
 * @returns {HTMLElement} - The timeline entry element
 */
export function createTimelineEntryElement(entry, formatTimeOnly) {
  const {
    block_id,
    block_type,
    section_title,
    version,
    edited_at,
    edited_by_name,
    is_current
  } = entry

  const formattedTime = formatTimeOnly ? formatTimeOnly(edited_at) : ''
  const blockLabel = section_title || (block_type ? block_type.replace(/_/g, ' ') : 'block')

  // Create container
  const container = document.createElement('div')
  container.className = `timeline-entry group relative py-3 px-3 -mx-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 border-l-4 ${is_current ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}`
  container.dataset.blockId = block_id
  container.dataset.version = version
  container.dataset.isCurrent = is_current
  container.dataset.action = 'click->admin--block-history#selectTimelineEntry'

  // Create flex wrapper
  const flexWrapper = document.createElement('div')
  flexWrapper.className = 'flex items-start gap-3'

  // Time column
  const timeDiv = document.createElement('div')
  timeDiv.className = 'flex-shrink-0 w-10 text-xs text-gray-400 pt-0.5'
  timeDiv.textContent = formattedTime
  flexWrapper.appendChild(timeDiv)

  // Content column
  const contentDiv = document.createElement('div')
  contentDiv.className = 'flex-1 min-w-0'

  // Title row
  const titleRow = document.createElement('div')
  titleRow.className = 'flex items-center gap-2'

  const titleSpan = document.createElement('span')
  titleSpan.className = 'font-medium text-sm text-gray-900 truncate'
  titleSpan.textContent = blockLabel // Safe: textContent
  titleRow.appendChild(titleSpan)

  if (is_current) {
    const badge = document.createElement('span')
    badge.className = 'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700'
    badge.textContent = 'Current'
    titleRow.appendChild(badge)
  }
  contentDiv.appendChild(titleRow)

  // Subtitle row
  const subtitleP = document.createElement('p')
  subtitleP.className = 'text-xs text-gray-500 mt-0.5'
  subtitleP.textContent = `v${version} by ${edited_by_name || 'Unknown'}`
  contentDiv.appendChild(subtitleP)

  flexWrapper.appendChild(contentDiv)

  // Restore button (only for non-current)
  if (!is_current) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-blue-600 hover:bg-blue-100 rounded'
    button.dataset.action = 'click->admin--block-history#restoreFromTimeline'
    button.dataset.blockId = block_id
    button.dataset.version = version
    button.title = 'Restore this version'
    button.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>'
    flexWrapper.appendChild(button)
  }

  container.appendChild(flexWrapper)
  return container
}

/**
 * Create a version card element safely
 *
 * @param {Object} entry - Version entry data
 * @param {number} entry.version - Version number
 * @param {string} entry.edited_at - ISO date string
 * @param {string} entry.edited_by_name - Editor name (user content)
 * @param {boolean} entry.isCurrent - Whether this is the current version
 * @param {Function} formatDate - Date formatter function
 * @param {Function} formatRelativeTime - Relative time formatter function
 * @returns {HTMLElement} - The version card element
 */
export function createVersionCardElement(entry, formatDate, formatRelativeTime) {
  const { version, edited_at, edited_by_name, isCurrent } = entry

  const formattedDate = formatDate ? formatDate(edited_at) : ''
  const relativeTime = formatRelativeTime ? formatRelativeTime(edited_at) : ''

  // Create container
  const container = document.createElement('div')
  container.className = `version-card group relative p-4 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${isCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`
  container.dataset.version = version
  container.dataset.isCurrent = isCurrent
  container.dataset.action = 'click->admin--block-history#selectVersion'

  // Flex wrapper
  const flexWrapper = document.createElement('div')
  flexWrapper.className = 'flex items-start justify-between'

  // Left column (version info)
  const leftCol = document.createElement('div')
  leftCol.className = 'flex-1 min-w-0'

  // Version title row
  const titleRow = document.createElement('div')
  titleRow.className = 'flex items-center gap-2'

  const versionSpan = document.createElement('span')
  versionSpan.className = 'font-medium text-sm text-gray-900'
  versionSpan.textContent = `Version ${version}`
  titleRow.appendChild(versionSpan)

  if (isCurrent) {
    const badge = document.createElement('span')
    badge.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
    badge.textContent = 'Current'
    titleRow.appendChild(badge)
  }
  leftCol.appendChild(titleRow)

  // Date
  const dateP = document.createElement('p')
  dateP.className = 'text-xs text-gray-500 mt-1'
  dateP.textContent = formattedDate
  leftCol.appendChild(dateP)

  // Relative time
  if (relativeTime) {
    const relTimeP = document.createElement('p')
    relTimeP.className = 'text-xs text-gray-400'
    relTimeP.textContent = relativeTime
    leftCol.appendChild(relTimeP)
  }

  flexWrapper.appendChild(leftCol)

  // Right column (editor name + restore button)
  const rightCol = document.createElement('div')
  rightCol.className = 'flex items-center gap-2'

  const editorDiv = document.createElement('div')
  editorDiv.className = 'text-right'

  const editorP = document.createElement('p')
  editorP.className = 'text-xs font-medium text-gray-700'
  editorP.textContent = edited_by_name || 'Unknown' // Safe: textContent
  editorDiv.appendChild(editorP)
  rightCol.appendChild(editorDiv)

  // Restore button
  if (!isCurrent) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-blue-600 hover:bg-blue-100 rounded'
    button.dataset.action = 'click->admin--block-history#restoreVersion'
    button.dataset.version = version
    button.title = 'Restore this version'
    button.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>'
    rightCol.appendChild(button)
  }

  flexWrapper.appendChild(rightCol)
  container.appendChild(flexWrapper)

  return container
}

/**
 * Create a date separator element
 * @param {string} dateText - The formatted date text
 * @returns {HTMLElement} - The date separator element
 */
export function createDateSeparator(dateText) {
  const div = document.createElement('div')
  div.className = 'text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-2 bg-gray-50 -mx-4 px-4 sticky top-0'
  div.textContent = dateText // Safe: textContent
  return div
}

/**
 * Create a sandboxed preview iframe for potentially unsafe HTML content
 *
 * This provides XSS protection by rendering user content in a sandboxed iframe
 * that cannot access the parent page's JavaScript context.
 *
 * @param {string} html - The HTML content to preview (may contain user content)
 * @param {Object} options - Configuration options
 * @param {string} options.width - Iframe width (default: '100%')
 * @param {string} options.height - Iframe height (default: 'auto')
 * @param {number} options.scale - Scale factor for preview (default: 0.4)
 * @returns {HTMLElement} - The preview wrapper element
 */
export function createSandboxedPreview(html, options = {}) {
  const { width: _width = '100%', height = 'auto', scale = 0.4 } = options

  const wrapper = document.createElement('div')
  wrapper.className = 'preview-wrapper relative rounded-lg border border-gray-200 overflow-hidden bg-white'

  const iframe = document.createElement('iframe')
  iframe.className = 'preview-iframe'
  iframe.style.width = `${100 / scale}%`
  iframe.style.height = height
  iframe.style.transform = `scale(${scale})`
  iframe.style.transformOrigin = 'top left'
  iframe.style.border = 'none'
  iframe.style.pointerEvents = 'none' // Prevent interactions

  // Sandbox attribute prevents script execution and form submission
  // Allow only what's needed for rendering (styles)
  iframe.sandbox = 'allow-same-origin' // Needed for CSS to work

  // Use srcdoc for secure content injection
  const safeHtmlDoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { margin: 0; overflow: hidden; font-family: system-ui, -apple-system, sans-serif; }
          * { box-sizing: border-box; }
          img { max-width: 100%; height: auto; }
        </style>
      </head>
      <body>${html}</body>
    </html>
  `
  iframe.srcdoc = safeHtmlDoc

  wrapper.appendChild(iframe)
  return wrapper
}

/**
 * Create a JSON content preview element safely
 * @param {Object} content - The content object to display
 * @returns {HTMLElement} - The preview element
 */
export function createJsonPreview(content) {
  // Filter out AI metadata
  const displayContent = { ...content }
  delete displayContent.ai_generated
  delete displayContent.ai_provider
  delete displayContent.ai_model
  delete displayContent.ai_prompt

  const wrapper = document.createElement('div')
  wrapper.className = 'p-4 bg-gray-50 rounded-lg border border-gray-200 overflow-auto max-h-[200px]'

  const pre = document.createElement('pre')
  pre.className = 'text-xs text-gray-700 whitespace-pre-wrap'
  // JSON.stringify is safe for JSON, but we use textContent for HTML safety
  pre.textContent = JSON.stringify(displayContent, null, 2)

  wrapper.appendChild(pre)
  return wrapper
}

/**
 * Create an empty preview placeholder
 * @param {string} message - The placeholder message
 * @returns {HTMLElement} - The placeholder element
 */
export function createEmptyPreview(message = 'No preview available') {
  const div = document.createElement('div')
  div.className = 'p-4 text-center text-gray-500 text-sm'
  div.textContent = message
  return div
}

logger.log('Safe HTML utilities loaded')
